import { useState } from "react";

import { Input } from "../../ui/input.tsx";
import { Label } from "../../ui/label.tsx";
import { Button } from "../../ui/button.tsx";
import { Separator } from "../../ui/separator.tsx";
import { ScrollArea } from "../../ui/scroll-area.tsx";

import { WebhookItem } from "./WebhookItem.tsx";
import { DeleteWebhook } from "./DeleteWebhook.tsx";
import { WebhookDeliveries } from "./WebhookDeliveries.tsx";

import { useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import { useApi } from "../../../Api/useApi.ts";
import { WebhookCreateType } from "../../../Api/types.ts";
import { useFormEditorContext } from "../../../Contexts/FormEditorContext.jsx";

export const Webhooks = () => {
    const api = useApi();
    const { id } = useParams<{ id: string }>();
    const { formSettings, setFormSettings } = useFormEditorContext();

    const [webhookUrl, setWebhookUrl] = useState<string>("");
    const [openDeliveries, setOpenDeliveries] = useState<boolean>(false);
    const [selectedWebhookId, setSelectedWebhookId] = useState<string>("");
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
    const [selectedWebhookUrl, setSelectedWebhookUrl] = useState<string>("");

    const createWebhookMutation = useMutation({
        mutationFn: ({
            formId,
            data,
        }: {
            formId: string;
            data: WebhookCreateType;
        }) => api.integrations.createWebhook(formId, data),
        onSuccess: (data) => {
            setFormSettings((prev) => ({
                ...prev,
                integrations: {
                    ...prev.integrations,
                    webhook_integration: [
                        ...(prev.integrations?.webhook_integration || []),
                        {
                            id: data?.data?.id,
                            url: data?.data?.url,
                            event: data?.data?.event,
                            event_filters: data?.data?.event_filters,
                            status: data?.data?.status,
                        },
                    ],
                },
            }));
        },

        onError: (error) => {
            console.error("Error details:", error);
        },
    });

    const handleCreateWebhook = () => {
        if (webhookUrl.trim()) {
            createWebhookMutation.mutate({
                formId: id as string,
                data: {
                    url: webhookUrl,
                    event: "response_received",
                    event_filters: ["partial", "completed"],
                },
            });
            setWebhookUrl("");
        }
    };

    return (
        <div className="w-full space-y-4 border-t h-full border-t-[#F1F2F4] ">
            <div className="flex-col">
                <div className="flex flex-col gap-1 px-3 pt-3 mb-4">
                    <Label>Webhooks</Label>
                    <Label className="text-xs font-normal text-[#44546F]">
                        Notify external services when someone completes your
                        BuildForm.
                    </Label>
                </div>
                <Separator className="mb-0 " />
            </div>

            <ScrollArea className="w-full h-[398px]">
                {formSettings?.integrations?.webhook_integration?.map(
                    (webhook) => (
                        <WebhookItem
                            key={webhook.id}
                            webhook={webhook}
                            setSelectedWebhookId={setSelectedWebhookId}
                            setOpenDeleteDialog={setOpenDeleteDialog}
                            setOpenDeliveries={setOpenDeliveries}
                            setSelectedWebhookUrl={setSelectedWebhookUrl}
                        />
                    ),
                )}

                {/* Add new */}
                <div className="flex-col mt-4 ">
                    <div className="flex flex-col px-4 pb-4">
                        <div className="flex items-center">
                            <div className="flex flex-col gap-1 mb-4">
                                <Label>Add a Webhook</Label>
                                <Label className="text-xs font-normal text-[#44546F]">
                                    We&rsquo;ll send your webhook to this URL
                                </Label>
                            </div>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                            <Input
                                className="w-full h-9 focus:border-[#e3e3e3] border-[#E4E4E7] focus-visible:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 "
                                value={webhookUrl}
                                onChange={(e) => setWebhookUrl(e.target.value)}
                                placeholder="https://example.com/webhook"
                                type="url"
                                pattern="https://.*"
                                title="Please enter a valid URL starting with https://"
                                required
                            />
                            <Button
                                disabled={!webhookUrl.trim()}
                                className="bg-[#191B33] text-white rounded-[8px] h-9 w-[102px] font-normal text-xs"
                                onClick={handleCreateWebhook}
                                type="button"
                            >
                                Save Webhook
                            </Button>
                        </div>
                    </div>
                    <Separator />
                </div>
            </ScrollArea>
            {/* Delete Webhook */}
            <DeleteWebhook
                open={openDeleteDialog}
                onOpenChange={setOpenDeleteDialog}
                webhookId={selectedWebhookId}
            />
            {/* Webhook Deliveries */}
            <WebhookDeliveries
                open={openDeliveries}
                onOpenChange={setOpenDeliveries}
                webhookId={selectedWebhookId}
                webhookUrl={selectedWebhookUrl}
            />
        </div>
    );
};
